import React, { FC } from "react"
import { Card, Descriptions, Space } from "antd"
import { ColumnsType } from "antd/lib/table"
import classnames from "classnames"
import { blue } from "@ant-design/colors"
import { GetCourseListWithPaginationPublicQuery } from "../../generated/graphql"
import { renderCourseType } from "../../views/TableColumnRenderers/TableColumnRenderers"
import { getTextColor } from "../../helpers/getTextColor"
import { margin } from "../../styles/layout"
import BookingButton from "./BookingButton"
import css from "./CourseTable.module.css"

export type CourseCardItem = GetCourseListWithPaginationPublicQuery["coursesWithPaginationPublic"]["items"][0]

type CardHeaderProps = {
    courseType: {
        name: string
        color?: string
        style?: React.CSSProperties
    }
    prefixedCourseNumber?: string
}

export const CardHeader: FC<CardHeaderProps> = ({ courseType: { name, color, style }, prefixedCourseNumber }) => {
    const actualStyle: React.CSSProperties = {
        padding: 0,
        fontSize: "0.95rem",
        minWidth: "50%",
        ...style,
    }
    return (
        <Space size='small' className={css.cardHeader}>
            <div className={css.cardHeaderItem}>{renderCourseType({ name, color, style: actualStyle })}</div>
            <div
                style={{
                    color: getTextColor({ name: color, value: color ? color : blue[3] }),
                }}
                className={classnames(css.cardHeaderItem, css.subCardHeader)}
            >
                {prefixedCourseNumber}
            </div>
        </Space>
    )
}

export const descriptionStyle = {
    bordered: true,
    contentStyle: { paddingLeft: 10 },
    labelStyle: { paddingLeft: 10, fontWeight: 600 },
}

type CourseCardProps = {
    course: CourseCardItem
    columnsToUse: ColumnsType<CourseCardItem>
    className?: string
    // Anker-id für die Scroll-zurück-UX (wird auf das Card-Root-div gespreizt).
    id?: string
}

const CourseCard: FC<CourseCardProps> = ({ course, columnsToUse, className, id }) => {
    return (
        <Card
            id={id}
            title={
                <CardHeader
                    courseType={{
                        name: course.courseType.name,
                        color: course.courseType.color,
                    }}
                    prefixedCourseNumber={course.prefixedCourseNumber ?? ""}
                />
            }
            key={course.id}
            bordered={false}
            className={classnames(css.card, className)}
            size='small'
            headStyle={{
                background: course.courseType.color,
            }}
        >
            <Descriptions
                bordered={descriptionStyle.bordered}
                size='small'
                column={1}
                contentStyle={descriptionStyle.contentStyle}
                labelStyle={descriptionStyle.labelStyle}
                className={css.descriptions}
            >
                {columnsToUse.map((column, index) => {
                    const label = column.title as string
                    const dataIndex = (column.key ?? "") as string
                    const ommitedColumns = ["courseType", "actionColumn", "prefixedCourseNumber"]
                    const typedDataIndex = dataIndex as keyof CourseCardItem
                    const renderedColumn: React.ReactNode = column?.render
                        ? (column.render(column.key ? course[typedDataIndex] : "_", course, index) as React.ReactNode)
                        : null
                    // Kurse ohne Beschränkung bekommen keine leere Zeile „Voraussetzungen“.
                    if (dataIndex === "restrictions" && !renderedColumn) return null
                    if (!ommitedColumns.includes(dataIndex)) {
                        if (column?.render) {
                            return (
                                <Descriptions.Item key={dataIndex} label={label}>
                                    {renderedColumn}
                                </Descriptions.Item>
                            )
                        }
                    }
                    return null
                })}
            </Descriptions>
            <div className={css.cardFooter}>
                <BookingButton
                    data-testid='booking-button'
                    block
                    record={course}
                    style={{
                        margin: 0 - 5,
                        marginTop: margin.s,
                        justifySelf: "center",
                        flex: 1,
                        width: "100%",
                    }}
                />
            </div>
        </Card>
    )
}

export default CourseCard
